import { useCallback, useEffect, useRef, useState } from 'react';
import type { CfbVisionIntakeBatch } from '../cfbScheduleVisionTypes';
import {
  CFB_SCHEDULE_INTAKE_MAX_POLL_ATTEMPTS,
  CFB_SCHEDULE_INTAKE_POLL_INTERVAL_MS,
  intakeBatchPollComplete,
  intakeBatchPollFailed,
  intakeBatchPollingInProgress,
} from '../cfbScheduleIntakePolling';

interface UseCfbScheduleIntakeBatchPollingOptions {
  batch: CfbVisionIntakeBatch | null;
  onRefresh: () => Promise<void>;
}

export function useCfbScheduleIntakeBatchPolling({
  batch,
  onRefresh,
}: UseCfbScheduleIntakeBatchPollingOptions) {
  const [active, setActive] = useState(() => (batch ? intakeBatchPollingInProgress(batch) : false));
  const [pollError, setPollError] = useState<string | null>(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!batch) {
      setActive(false);
      return;
    }
    setActive(intakeBatchPollingInProgress(batch));
  }, [batch?.id, batch?.pipeline_stage, batch?.status, batch?.pipeline_progress]);

  useEffect(() => {
    if (!batch || !active) {
      return undefined;
    }

    if (intakeBatchPollComplete(batch)) {
      setActive(false);
      if (intakeBatchPollFailed(batch)) {
        setPollError(batch.error_message || 'VOD schedule extraction failed.');
      }
      return undefined;
    }

    let cancelled = false;
    const intervalId = window.setInterval(() => {
      if (cancelled || document.hidden) {
        return;
      }

      attemptsRef.current += 1;
      if (attemptsRef.current > CFB_SCHEDULE_INTAKE_MAX_POLL_ATTEMPTS) {
        setActive(false);
        setPollError(
          'Extraction is taking longer than expected. Refresh the page to check status.'
        );
        return;
      }

      void onRefresh().catch(() => {
        if (!cancelled) {
          setPollError('Could not refresh VOD intake status.');
        }
      });
    }, CFB_SCHEDULE_INTAKE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [active, batch, onRefresh]);

  const clearPollError = useCallback(() => {
    setPollError(null);
  }, []);

  const restartPolling = useCallback(() => {
    attemptsRef.current = 0;
    setPollError(null);
    setActive(true);
  }, []);

  const polling = Boolean(batch && active && intakeBatchPollingInProgress(batch));

  return {
    polling,
    pollError,
    clearPollError,
    restartPolling,
  };
}
